import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { ArrowLeft, Eye, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../components/UI/Modal';

const Container = styled.div`
  padding: 2rem;
  background-color: ${props => props.theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const PreviewCard = styled(Card)`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const PreviewHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
`;

const PreviewTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const PreviewDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0.5rem 0;
  line-height: 1.6;
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  .required {
    color: ${props => props.theme.colors.error};
    margin-left: 0.25rem;
  }
`;

const FieldHelpText = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-top: 0.25rem;
  font-style: italic;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  font-size: 1rem;
  color: ${props => props.theme.colors.text.primary};
  background: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &:disabled {
    background: ${props => props.theme.colors.background.secondary};
    cursor: not-allowed;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  font-size: 1rem;
  color: ${props => props.theme.colors.text.primary};
  background: ${props => props.theme.colors.white};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &:disabled {
    background: ${props => props.theme.colors.background.secondary};
    cursor: not-allowed;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  font-size: 1rem;
  color: ${props => props.theme.colors.text.primary};
  background: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &:disabled {
    background: ${props => props.theme.colors.background.secondary};
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PreviewNote = styled.div`
  padding: 1rem;
  background: ${props => props.theme.colors.warning}20;
  border: 1px solid ${props => props.theme.colors.warning};
  border-radius: 8px;
  margin-bottom: 2rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.primary};
`;

const MembershipApplicationFormPreview = ({ formId, onClose }) => {
  const { theme } = useTheme();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState({});

  useEffect(() => {
    if (formId) {
      fetchForm();
    }
  }, [formId]);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const res = await membershipApplicationAPI.getForm(formId);
      setForm(res.data);
      
      // Initialize preview data with empty values
      const initialData = {};
      const fields = res.data.activeFields || res.data.fields || [];
      fields.forEach(field => {
        if (field.field_type === 'checkbox') {
          initialData[field.field_name] = [];
        } else {
          initialData[field.field_name] = '';
        }
      });
      setPreviewData(initialData);
    } catch (error) {
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setPreviewData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const renderField = (field) => {
    const value = previewData[field.field_name] || '';
    
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
        return (
          <StyledInput
            type={field.field_type === 'tel' ? 'tel' : field.field_type === 'number' ? 'number' : field.field_type}
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || ''}
            disabled
            theme={theme}
          />
        );
      
      case 'textarea':
        return (
          <StyledTextarea
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || ''}
            disabled
            theme={theme}
          />
        );
      
      case 'select':
        return (
          <StyledSelect
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            disabled
            theme={theme}
          >
            <option value="">{field.placeholder || 'Select an option...'}</option>
            {Array.isArray(field.options) && field.options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </StyledSelect>
        );
      
      case 'checkbox':
        return (
          <CheckboxGroup theme={theme}>
            {Array.isArray(field.options) && field.options.map((option, idx) => (
              <CheckboxOption key={idx} theme={theme}>
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={() => {
                    const current = Array.isArray(value) ? value : [];
                    const updated = current.includes(option)
                      ? current.filter(v => v !== option)
                      : [...current, option];
                    handleInputChange(field.field_name, updated);
                  }}
                  disabled
                />
                <span>{option}</span>
              </CheckboxOption>
            ))}
          </CheckboxGroup>
        );
      
      case 'date':
        return (
          <StyledInput
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            disabled
            theme={theme}
          />
        );
      
      case 'file':
        return (
          <StyledInput
            type="file"
            disabled
            theme={theme}
            style={{ padding: '0.5rem' }}
          />
        );
      
      default:
        return (
          <StyledInput
            type="text"
            value={value}
            placeholder={field.placeholder || ''}
            disabled
            theme={theme}
          />
        );
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading form preview...
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Form not found
      </div>
    );
  }

  return (
    <PreviewCard theme={theme}>
      <PreviewHeader theme={theme}>
        <PreviewTitle theme={theme}>{form.title}</PreviewTitle>
        {form.description && (
          <PreviewDescription theme={theme}>{form.description}</PreviewDescription>
        )}
        {form.instructions && (
          <PreviewDescription theme={theme} style={{ fontSize: '0.875rem', marginTop: '1rem', padding: '1rem', background: theme.colors.background.secondary, borderRadius: '8px' }}>
            <strong>Instructions:</strong> {form.instructions}
          </PreviewDescription>
        )}
      </PreviewHeader>

      <PreviewNote theme={theme}>
        <strong>Preview Mode:</strong> This is how the form will appear to users. Fields are disabled in preview.
      </PreviewNote>

      <form>
        {(form.activeFields && form.activeFields.length > 0) || (form.fields && form.fields.length > 0) ? (
          (form.activeFields || form.fields || []).map((field, index) => (
            <FormField key={field.id || index} theme={theme}>
              <FieldLabel theme={theme}>
                {field.field_label}
                {field.is_required && <span className="required">*</span>}
              </FieldLabel>
              {renderField(field)}
              {field.help_text && (
                <FieldHelpText theme={theme}>{field.help_text}</FieldHelpText>
              )}
            </FormField>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
            No fields configured for this form.
          </div>
        )}
      </form>
    </PreviewCard>
  );
};

export default MembershipApplicationFormPreview;

